import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import type {ReviewSummaryState, ConversationReviewSummary} from '../../page-data/payloads/conversation-comments'
import {useReactToReviewSummaryMutation} from '../../mutations/use-react-to-review-summary-mutation'
import {ReviewApprovedIcon} from '@github-ui/custom-icons/ReviewApprovedIcon'
import {ReviewChangesRequestedIcon} from '@github-ui/custom-icons/ReviewChangesRequestedIcon'
import {ReviewCommentedIcon} from '@github-ui/custom-icons/ReviewCommentedIcon'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {useRef, useMemo, use} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {StatusAvatar} from '@github-ui/status-avatar/StatusAvatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link, useMergedRefs} from '@primer/react'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {OnBehalfOfTeams} from '../overview/reviewers/OnBehalfOfTeams'
import {legacyReviewerToProto} from '../../page-data/payloads/review-requests'
import {clsx} from 'clsx'
import {ThreadPreview} from './ThreadPreview'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import type {ReviewThread} from './utils/assign-threads-to-reviews'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import {reviewSummaryAnchor} from './utils/comment-anchors'
import {CommentsContext} from './CommentsContext'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'
import styles from './ReviewSummaryItem.module.css'
import {announce} from '@github-ui/aria-live'
import {ConversationItemBody} from './ConversationItemBody'
import {useBodyEditManager} from './use-body-edit-manager'
import {useUpdateReviewSummaryMutation} from '../../mutations/use-update-review-summary-mutation'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ConversationItemFallback} from './ConversationItemFallback'
import {useReturnFocus} from '@github-ui/use-return-focus'

const reviewSummaryStateLabels: Record<ReviewSummaryState, string> = {
  COMMENTED: 'reviewed',
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'requested changes',
  DISMISSED: 'previously reviewed',
}

interface ReviewSummaryItemProps {
  reviewSummary: ConversationReviewSummary
  reviewThreads?: ReviewThread[]
  pullRequestAuthorLogin: string
  // Optional flag to show the review state icon next to the review summary. Defaults to false.
  withReviewStateIcon?: boolean
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
  onNavigateToDiffComment?: () => void
}

function getReviewStateVisual(state: ReviewSummaryState) {
  switch (state) {
    case 'APPROVED':
      return {
        icon: ReviewApprovedIcon,
        className: styles.approvedStatus,
      }
    case 'CHANGES_REQUESTED':
      return {
        icon: ReviewChangesRequestedIcon,
        className: styles.changesRequestedStatus,
      }
    case 'COMMENTED':
      return {
        icon: ReviewCommentedIcon,
        className: styles.commentedStatus,
      }
    case 'DISMISSED':
    default:
      return null
  }
}

function UnsafeReviewSummaryItem({
  reviewSummary,
  pullRequestAuthorLogin,
  onQuoteReply: externalOnQuoteReply,
  withReviewStateIcon,
  className,
  reviewThreads,
  onNavigateToDiffComment,
  id,
}: ReviewSummaryItemProps & {id: string}) {
  const commentRef = useRef<HTMLElement>(null)
  const canComment = useLayoutCanComment()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const reactToReviewSummaryMutation = useReactToReviewSummaryMutation()

  const hasBody = reviewSummary.body.trim().length > 0

  const onQuoteReply = useMemo(() => {
    if (!canComment || !externalOnQuoteReply || !hasBody) return undefined

    return () => {
      const current = ssrSafeWindow?.getSelection()
      let currentSelection: SelectionContext | null = null

      if (current && current.anchorNode && current.rangeCount > 0) {
        currentSelection = {
          anchorNode: current.anchorNode,
          range: current.getRangeAt(0),
        }
      }

      const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, reviewSummary.body)
      externalOnQuoteReply(quotedText ?? reviewSummary.body)
    }
  }, [canComment, externalOnQuoteReply, hasBody, reviewSummary.body])
  const reviewStateVisual = withReviewStateIcon ? getReviewStateVisual(reviewSummary.state) : null
  const isHidden = reviewSummary.isHidden || Boolean(reviewSummary.minimizedReason)

  const threads = useMemo(() => reviewThreads?.map(({thread}) => thread) ?? [], [reviewThreads])
  const hasThreads = threads.length > 0
  const reviewSummaryId = reviewSummary.id

  const threadsElement = (
    <>
      <HydrateThreadPositioning threads={threads} />
      {reviewThreads?.map(({thread, anchorPrefix}) => (
        <ThreadPreview
          key={thread.id}
          thread={thread}
          // The same thread may appear under multiple reviews, so we attach a review ID prefix to ensure uniqueness
          anchorPrefix={anchorPrefix}
          selectedReviewId={reviewSummaryId}
          focusOnSelectedReview={thread.commentsData?.comments?.[0]?.reviewId !== reviewSummaryId}
          onNavigateToDiffComment={onNavigateToDiffComment}
          headingLevel="h2"
        />
      ))}
    </>
  )

  const editBodyMutation = useUpdateReviewSummaryMutation({
    onSuccess: () => announce('Review updated successfully.'),
  })
  const directEditBodyMutation = useUpdateReviewSummaryMutation()
  const editManager = useBodyEditManager(reviewSummary, editBodyMutation, directEditBodyMutation)

  const {containerRef: editorContainerRef, returnFocusRef} = useReturnFocus()
  const combinedCommentRef = useMergedRefs(commentRef, returnFocusRef)

  // If there's only one thread and the review is COMMENTED without a body, depending on whether the thread belongs to
  // this review, render just the thread in a container for review navigation. Otherwise, render nothing.
  if (threads.length === 1 && reviewSummary.state === 'COMMENTED' && !hasBody) {
    if (threads[0]?.commentsData?.comments[0]?.reviewId === reviewSummaryId) {
      return <div id={id}>{threadsElement}</div>
    } else {
      return null
    }
  }

  if (!reviewSummary.repository) return null

  return (
    <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
      <CommentLayout
        ref={combinedCommentRef}
        className={clsx('border rounded-2', className)}
        tabIndex={-1}
        id={id}
        header={
          <>
            <ActivityHeader
              avatarUrl={reviewSummary.author?.avatarUrl || ''}
              userAvatar={
                reviewStateVisual ? (
                  <Link
                    href={`/${reviewSummary.author?.login ?? ''}`}
                    aria-label={`@${reviewSummary.author?.login ?? ''}'s profile`}
                    className={clsx(isHidden && styles.hiddenAvatar)}
                    {...getHovercardAttributesForType('user', {login: reviewSummary.author?.login})}
                  >
                    <StatusAvatar
                      altText={`@${reviewSummary.author?.login ?? ''}`}
                      src={reviewSummary.author?.avatarUrl ?? ''}
                      square={false}
                      icon={reviewStateVisual.icon}
                      iconColor="var(--review-state-icon-color)"
                      backgroundColor="var(--review-state-background-color)"
                      className={reviewStateVisual.className}
                      backgroundClassName={styles.statusBackground}
                      size={24}
                    />
                  </Link>
                ) : undefined
              }
              comment={{
                id: String(reviewSummary.id),
                authorAssociation: reviewSummary.authorAssociation,
                createdAt: reviewSummary.createdAt,
                isHidden: reviewSummary.isHidden,
                minimizedReason: reviewSummary.minimizedReason,
                repository: reviewSummary.repository,
                url: reviewSummary.url,
              }}
              commentAuthorLogin={reviewSummary.author?.login ?? ''}
              commentSubjectType="pull request"
              commentSubjectAuthorLogin={pullRequestAuthorLogin}
              forceInlineAvatar
              isMinimized={false}
              noContainerStyles
              tooltipDirection="sw"
              additionalHeaderMessage={<>{reviewSummaryStateLabels[reviewSummary.state]}&nbsp;</>}
              actions={
                editManager.isEditing ? (
                  <></>
                ) : (
                  <ConversationItemActions
                    authorLogin={reviewSummary.author?.login ?? ''}
                    contentId={reviewSummary.relayId}
                    contentUrl={reviewSummary.url}
                    link={reviewSummary.url}
                    onQuoteReply={onQuoteReply}
                    onEdit={hasBody ? editManager.startEditIfAllowed : undefined}
                    organizationLogin={reviewSummary.repository?.owner.login}
                    viewerCanBlockFromOrg={reviewSummary.viewerCanBlockFromOrg}
                    viewerCanBlockPersonally={reviewSummary.viewerCanBlockPersonally}
                    viewerCanUnblockFromOrg={reviewSummary.viewerCanUnblockFromOrg}
                    viewerCanUnblockPersonally={reviewSummary.viewerCanUnblockPersonally}
                  />
                )
              }
            />
            <OnBehalfOfTeams
              teams={reviewSummary.onBehalfOfTeams.map(legacyReviewerToProto)}
              className="pt-1"
              withAvatar={false}
            />
          </>
        }
      >
        {(hasBody || hasThreads) && (
          <div className="d-flex flex-column position-relative width-full gap-3">
            {hasBody && (
              <ConversationItemBody
                item={reviewSummary}
                reactMutation={reactToReviewSummaryMutation}
                editManager={editManager}
                editorContainerRef={editorContainerRef}
              />
            )}
            {hasThreads && threadsElement}
          </div>
        )}
      </CommentLayout>
    </ConversationItemActionsProvider>
  )
}

export function ReviewSummaryItem({reviewSummary, className, ...props}: ReviewSummaryItemProps) {
  const anchorPrefix = use(CommentsContext).commentAnchorPrefix
  const id = reviewSummaryAnchor(String(reviewSummary.id), anchorPrefix)
  useFocusHashTarget(id)

  return (
    <ErrorBoundary
      fallback={<ConversationItemFallback tabIndex={-1} id={id} item={reviewSummary} className={className} />}
      boundaryName="IssueCommentItem"
    >
      <UnsafeReviewSummaryItem id={id} reviewSummary={reviewSummary} className={className} {...props} />
    </ErrorBoundary>
  )
}
